import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { cantieri, fatture, clienti } from '../utils/api';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, Legend } from 'recharts';
import SelectorePeriodo from '../components/SelectorePeriodo';
import useIsMobile from '../hooks/useIsMobile';

const fmt = (n) => n == null ? '—' : Number(n).toLocaleString('it-IT');
const fmtEur = (n) => n == null ? '—' : `€ ${Number(n).toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtPct = (n) => n == null ? '—' : `${Number(n).toFixed(1)}%`;
const todayISO = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; };
const nowTime = () => new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });

/* ─── Palette accenti per icone sezione/card ─── */
const ACCENT = {
  ops:    { bg: '#EDE9FE', ink: '#6D28D9' },
  fin:    { bg: '#E6F9F1', ink: '#0D6E48' },
  cash:   { bg: '#DBEAFE', ink: '#1D4ED8' },
  prev:   { bg: '#FCE7F3', ink: '#BE185D' },
  warn:   { bg: '#FEF3C7', ink: '#92500A' },
  danger: { bg: '#FEE2E2', ink: '#991B1B' },
  info:   { bg: '#DBEAFE', ink: '#1D4ED8' },
};

/* ─── Tooltip "i" inline ─── */
function InfoTooltip({ text }) {
  const [show, setShow] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!show) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setShow(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [show]);
  return (
    <div ref={ref} style={{ position: 'absolute', top: 16, right: 16, zIndex: 10 }}>
      <div
        onClick={(e) => { e.stopPropagation(); setShow(v => !v); }}
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        style={{
          width: 18, height: 18, borderRadius: '50%', border: '1px solid rgb(var(--border))',
          display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
          fontSize: 11, fontWeight: 500, color: 'rgb(var(--muted))', background: 'rgb(var(--bg))',
          lineHeight: 1, userSelect: 'none',
        }}>i</div>
      {show && (
        <div style={{
          position: 'absolute', bottom: 'calc(100% + 6px)', right: 0,
          background: '#2C2C2A', color: '#fff', borderRadius: 8,
          padding: '8px 10px', fontSize: 11, lineHeight: 1.4,
          maxWidth: 220, width: 'max-content', boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
          zIndex: 20, pointerEvents: 'none',
        }}>{text}</div>
      )}
    </div>
  );
}

/* ─── Section header ─── */
function SectionHeader({ icon, accent, title, subtitle, counter, delay = 0 }) {
  const a = ACCENT[accent] || ACCENT.fin;
  return (
    <div className="gov-reveal" style={{ animationDelay: `${delay}s`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '32px 0 14px', marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{ width: 40, height: 40, borderRadius: 12, background: a.bg, color: a.ink, display: 'grid', placeItems: 'center', flexShrink: 0 }}>{icon}</div>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 500, letterSpacing: '-0.01em', margin: 0, color: 'rgb(var(--text))' }}>{title}</h2>
          <div style={{ fontSize: 12, color: 'rgb(var(--muted))', marginTop: 2 }}>{subtitle}</div>
        </div>
      </div>
      <span className="num" style={{ fontSize: 12, color: 'rgb(var(--muted))', letterSpacing: '0.04em' }}>{counter}</span>
    </div>
  );
}

/* ─── Card KPI ─── */
function KpiCard({ icon, accent = 'fin', label, value, valueColor, delta, deltaTone, sub, extra, tooltip, delay = 0, onClick }) {
  const a = ACCENT[accent] || ACCENT.fin;
  const deltaPalette = {
    up:   { bg: '#E6F9F1', ink: '#0D6E48' },
    dn:   { bg: '#FEE2E2', ink: '#991B1B' },
    flat: { bg: 'rgba(43,46,54,0.06)', ink: 'rgb(var(--muted))' },
  }[deltaTone || 'flat'];
  const clickable = typeof onClick === 'function';
  return (
    <div className="kpi gov-reveal"
      onClick={onClick}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
      onMouseEnter={clickable ? (e) => { e.currentTarget.style.borderColor = 'rgb(var(--text))'; e.currentTarget.style.boxShadow = '0 2px 4px rgba(43,46,54,0.06), 0 12px 30px -10px rgba(43,46,54,0.14)'; e.currentTarget.style.transform = 'translateY(-2px)'; } : undefined}
      onMouseLeave={clickable ? (e) => { e.currentTarget.style.borderColor = 'rgb(var(--border))'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(43,46,54,0.04), 0 8px 24px -10px rgba(43,46,54,0.08)'; e.currentTarget.style.transform = 'none'; } : undefined}
      style={{
      animationDelay: `${delay}s`,
      background: 'rgb(var(--surface))', border: '1px solid rgb(var(--border))', borderRadius: 16,
      padding: '22px 24px', minHeight: 140, display: 'flex', flexDirection: 'column',
      position: 'relative', boxShadow: '0 1px 2px rgba(43,46,54,0.04), 0 8px 24px -10px rgba(43,46,54,0.08)',
      cursor: clickable ? 'pointer' : 'default', transition: 'box-shadow 0.15s ease, border-color 0.15s ease, transform 0.15s ease',
    }}>
      {tooltip && <InfoTooltip text={tooltip} />}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingRight: tooltip ? 24 : 0 }}>
        {icon && <span style={{ width: 32, height: 32, borderRadius: 10, background: a.bg, color: a.ink, display: 'grid', placeItems: 'center', flexShrink: 0 }}>{icon}</span>}
        <span style={{ fontSize: 11, color: 'rgb(var(--muted))', fontWeight: 500 }}>{label}</span>
      </div>
      <div className="num font-display" style={{ fontSize: 30, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1, marginTop: 18, color: valueColor || 'rgb(var(--text))' }}>{value}</div>
      {delta && (
        <span className="num" style={{
          fontSize: 11, padding: '4px 9px', borderRadius: 999, display: 'inline-flex', alignItems: 'center', gap: 4,
          marginTop: 10, fontWeight: 500, alignSelf: 'flex-start', background: deltaPalette.bg, color: deltaPalette.ink,
        }}>{delta}</span>
      )}
      {extra}
      {sub && <p style={{ fontSize: 12, color: 'rgb(var(--muted))', marginTop: 'auto', paddingTop: 10, lineHeight: 1.5, marginBottom: 0 }}>{sub}</p>}
    </div>
  );
}

/* ─── Progress bar ─── */
function Progress({ pct, tone = 'ok' }) {
  const color = { ok: 'var(--brand)', warn: '#F59E0B', crit: '#EF4444' }[tone] || 'var(--brand)';
  return (
    <div style={{ height: 6, borderRadius: 999, background: 'rgba(43,46,54,0.06)', marginTop: 14, overflow: 'hidden' }}>
      <div style={{ width: `${Math.min(100, Math.max(0, pct || 0))}%`, height: '100%', borderRadius: 999, background: color, transition: 'width 0.8s cubic-bezier(0.16,1,0.3,1)' }} />
    </div>
  );
}

/* ─── Lista "ritardi" inline ─── */
function DelayList({ items, tone = 'crit' }) {
  const palette = tone === 'warn' ? { bg: '#FEF3C7', ink: '#92500A' } : { bg: '#FEE2E2', ink: '#991B1B' };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 14 }}>
      {items.map((it, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 12px', background: palette.bg, borderRadius: 10, fontSize: 12 }}>
          <span style={{ color: 'rgb(var(--text))', fontWeight: 500 }}>{it.label}</span>
          <span className="num" style={{ color: palette.ink, fontWeight: 500 }}>{it.delta}</span>
        </div>
      ))}
    </div>
  );
}

/* ─── Donut SVG (percentuale) ─── */
function Donut({ pct, label }) {
  const v = pct == null ? 0 : Math.max(0, Math.min(100, pct));
  return (
    <svg width={92} height={92} viewBox="0 0 36 36" aria-hidden="true">
      <circle cx="18" cy="18" r="15.9155" fill="none" stroke="rgba(43,46,54,0.08)" strokeWidth={10} />
      <circle cx="18" cy="18" r="15.9155" fill="none" stroke="var(--brand)" strokeWidth={10} strokeLinecap="round"
              strokeDasharray={`${v} 100`} pathLength={100} transform="rotate(-90 18 18)" />
      <text x="50%" y="55%" textAnchor="middle" className="num" style={{ fontSize: 9, fontWeight: 500, fill: 'rgb(var(--text))' }}>
        {label}
      </text>
    </svg>
  );
}

/* ─── Icone SVG inline ─── */
const I = {
  building: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21h18"/><path d="M5 21V7l7-4 7 4v14"/><path d="M9 9h1"/><path d="M14 9h1"/><path d="M9 13h1"/><path d="M14 13h1"/><path d="M9 17h6"/></svg>,
  trend:    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>,
  cash:     <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12V7a2 2 0 0 0-2-2H5a2 2 0 0 1 0-4h14v4"/><path d="M3 5v14a2 2 0 0 0 2 2h16v-5"/></svg>,
  doc:      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>,
  clock:    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  check:    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
  pause:    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>,
  euro:     <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
  trendDn:  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 17 13.5 8.5 8.5 13.5 2 7"/><polyline points="16 17 22 17 22 11"/></svg>,
  inbox:    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M2 10h20"/></svg>,
  funnel:   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 17 9 11 13 15 21 7"/><polyline points="14 7 21 7 21 14"/></svg>,
  warning:  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
  users:    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
};

export default function Governance() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [periodoParams, setPeriodoParams] = useState(() => {
    const now = new Date();
    return { periodo: 'mese', mese: now.getMonth() + 1, anno: now.getFullYear() };
  });
  const [loading, setLoading] = useState(true);
  const [govLoading, setGovLoading] = useState(true);
  const [errorAll, setErrorAll] = useState(false);
  const [mensile, setMensile] = useState([]);
  const [gov, setGov] = useState({
    incassi_mese: null, pipeline_valore: null, dso_giorni: null,
    conversione_preventivi: null, concentrazione_clienti: null, preventivi_in_scadenza: null,
  });
  const [data, setData] = useState({
    fatturato: null, costi: null, pending: null, scadute: null,
    ivaDebito: null, ivaCredito: null, ivaSaldo: null,
    cantieriAttivi: null, cantieriRitardo: null, cantieriCompletati: null, cantieriSospesi: null,
    cantieriRitardoList: [], cantieriAll: [],
  });
  const [funnel, setFunnel] = useState({
    prospect: null, trattativa: null, attivo: null,
    in_pausa: null, perso: null, totale: null,
  });
  const [funnelLoading, setFunnelLoading] = useState(true);
  const [funnelError, setFunnelError] = useState(false);

  // ── Cantieri (stato corrente, no periodo) ──
  useEffect(() => {
    localStorage.setItem('governance_visited', '1');
    (async () => {
      try {
        const cRes = await cantieri.list({ page_size: 200 });
        const items = Array.isArray(cRes.data?.results || cRes.data) ? (cRes.data.results || cRes.data) : [];
        const today = todayISO();
        const attivi = items.filter(c => ['nuovo', 'sopralluogo', 'in_corso'].includes(c.stato));
        const ritardo = items.filter(c =>
          c.data_fine_stimata && c.data_fine_stimata < today &&
          !['completato', 'annullato', 'sospeso'].includes(c.stato)
        );
        const completati = items.filter(c => c.stato === 'completato');
        const ritardoList = ritardo.slice(0, 3).map(c => {
          const diff = Math.ceil((new Date(today) - new Date(c.data_fine_stimata)) / 86400000);
          return { label: c.titolo, delta: `+${diff}gg` };
        });
        setData(prev => ({
          ...prev,
          cantieriAttivi: attivi.length,
          cantieriRitardo: ritardo.length,
          cantieriCompletati: completati.length,
          cantieriSospesi: items.filter(c => c.stato === 'sospeso').length,
          cantieriRitardoList: ritardoList,
          cantieriAll: items,
        }));
      } catch { /* fallback silenzioso */ }
    })();
  }, []);

  // ── Funnel commerciale (snapshot, no periodo — Step 2) ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setFunnelLoading(true); setFunnelError(false);
      try {
        const res = await clienti.funnel();
        if (cancelled) return;
        setFunnel(res.data || {});
      } catch {
        if (!cancelled) setFunnelError(true);
      } finally {
        if (!cancelled) setFunnelLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Sezioni filtrabili per periodo ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setGovLoading(true); setErrorAll(false);
      try {
        const [govRes, fatKpi, fatMensile] = await Promise.allSettled([
          fatture.governanceKpi(periodoParams),
          fatture.kpi(periodoParams),
          fatture.kpiMensile(periodoParams),
        ]);
        if (cancelled) return;
        const allFailed = [govRes, fatKpi, fatMensile].every(r => r.status === 'rejected');
        if (allFailed) setErrorAll(true);
        if (govRes.status === 'fulfilled') setGov(govRes.value.data);
        if (fatKpi.status === 'fulfilled') {
          const d = fatKpi.value.data;
          setData(prev => ({
            ...prev,
            fatturato: d.emesso_ytd ?? d.totale_fatturato ?? null,  // fonte unica: "Fatturato" = emesso
            costi: d.costi_ytd ?? null,
            pending: d.pending_totale ?? null,
            pendingInCorso: d.da_incassare_inviata ?? null,
            pendingScadute: d.da_incassare_scaduta ?? null,
            scadute: d.scadute_count ?? null,
            ivaDebito: d.iva_debito ?? null,
            ivaCredito: d.iva_credito ?? null,
            ivaSaldo: d.iva_saldo ?? null,
          }));
        }
        if (fatMensile.status === 'fulfilled') setMensile(fatMensile.value.data || []);
      } finally {
        if (!cancelled) { setLoading(false); setGovLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [periodoParams]);

  const onPeriodoChange = useCallback((params) => setPeriodoParams(params), []);

  const margineCalc = data.fatturato && data.costi != null
    ? ((data.fatturato - data.costi) / data.fatturato * 100) : null;

  const margineTone = margineCalc == null ? 'ok' : (margineCalc >= 52 ? 'ok' : margineCalc >= 40 ? 'warn' : 'crit');
  const dsoTone = gov.dso_giorni == null ? 'ok' : (gov.dso_giorni <= 45 ? 'ok' : gov.dso_giorni <= 60 ? 'warn' : 'crit');
  const concentrazioneTone = gov.concentrazione_clienti == null ? 'ok' : (gov.concentrazione_clienti >= 70 ? 'crit' : gov.concentrazione_clienti >= 55 ? 'warn' : 'ok');

  // Numerazione sezioni "0X / NN" e cascata reveal DERIVATE dalla posizione nel
  // JSX (voce 8, Q3-A): un riordino futuro è puro spostamento di blocchi — sia il
  // counter sia l'animationDelay si riallineano da soli, niente più valori
  // hardcoded da rinumerare a mano (il difetto che questo riordino aveva scoperto).
  // Deterministico: render single-pass top→bottom, i contatori ripartono ogni render.
  const TOTALE_SEZIONI = 5;
  const REVEAL_STEP = 0.04; // ~26 elementi → ultimo delay ~1.04s (envelope originale)
  let _sez = 0, _rev = 0;
  const sezNo = () => `${String(++_sez).padStart(2, '0')} / ${String(TOTALE_SEZIONI).padStart(2, '0')}`;
  const rev = () => Number((++_rev * REVEAL_STEP).toFixed(2));

  return (
    <div style={{ padding: isMobile ? '20px 16px 80px' : '24px 32px 80px', width: '100%', boxSizing: 'border-box' }}>

      {/* ── Hero ── */}
      <header className="gov-reveal" style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '3fr 1fr', gap: isMobile ? 14 : 24, alignItems: isMobile ? 'start' : 'center', marginBottom: 24, padding: '8px 0' }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 500, lineHeight: 1.2, letterSpacing: '-0.02em', margin: 0, color: 'rgb(var(--text))' }}>
            Controllo gestionale <em style={{ fontStyle: 'normal', color: 'rgb(var(--muted))', fontWeight: 400 }}>— una fotografia onesta dell'impresa.</em>
          </h1>
          <p style={{ fontSize: 14, color: 'rgb(var(--muted))', marginTop: 6, lineHeight: 1.5, marginBottom: 0 }}>
            Indicatori operativi, finanziari e di liquidità. Filtra per periodo per ricalcolare ogni cella.
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: isMobile ? 'flex-start' : 'flex-end', gap: 8 }}>
          <SelectorePeriodo onChange={onPeriodoChange} />
          <span style={{ fontSize: 11, color: 'rgb(var(--muted))', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {I.clock} Aggiornato alle {nowTime()}
          </span>
        </div>
      </header>

      {errorAll && (
        <div style={{ padding: '14px 18px', background: '#FEE2E2', color: '#991B1B', borderRadius: 12, border: '1px solid #FCA5A5', marginBottom: 20, fontSize: 13 }}>
          Impossibile caricare i KPI. Riprova tra qualche istante o ricarica la pagina.
        </div>
      )}

      {/* ═══ Operativa cantieri ═══ */}
      <SectionHeader icon={I.building} accent="ops" title="Operativa cantieri" subtitle="Stato lavori, ritardi e completamento" counter={sezNo()} delay={rev()} />
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 16, marginBottom: 8 }}>
        <KpiCard
          accent="ops" icon={I.building} label="Cantieri attivi"
          onClick={() => navigate('/cantieri?vista=attivi')}
          value={loading && data.cantieriAttivi == null ? '—' : fmt(data.cantieriAttivi)}
          tooltip="I lavori che hai in corso in questo momento"
          sub={data.cantieriAll.length > 0 ? (() => {
            const b = { in_corso: 0, sopralluogo: 0, nuovo: 0 };
            data.cantieriAll.forEach(c => { if (b[c.stato] != null) b[c.stato]++; });
            return `${b.in_corso} in corso · ${b.sopralluogo} sopralluogo · ${b.nuovo} nuovo.`;
          })() : null}
          delay={rev()}
        />
        <KpiCard
          accent="danger" icon={I.clock} label="In ritardo"
          onClick={() => navigate('/cantieri?vista=ritardo')}
          value={loading && data.cantieriRitardo == null ? '—' : fmt(data.cantieriRitardo)}
          valueColor="#991B1B"
          tooltip="Lavori che hanno superato la data di fine prevista"
          extra={data.cantieriRitardoList.length > 0 ? <DelayList items={data.cantieriRitardoList} tone="crit" /> : null}
          delay={rev()}
        />
        <KpiCard
          accent="fin" icon={I.check} label="Completati"
          onClick={() => navigate('/cantieri')}
          value={loading && data.cantieriCompletati == null ? '—' : fmt(data.cantieriCompletati)}
          tooltip="Cantieri chiusi"
          delay={rev()}
        />
        <KpiCard
          accent="warn" icon={I.pause} label="Sospesi"
          onClick={() => navigate('/cantieri')}
          value={loading && data.cantieriSospesi == null ? '—' : fmt(data.cantieriSospesi)}
          valueColor="#92500A"
          tooltip="Lavori temporaneamente bloccati"
          sub="Verifica causa e aggiorna il cliente."
          delay={rev()}
        />
      </div>

      {/* ═══ Funnel commerciale ═══ */}
      <SectionHeader icon={I.users} accent="prev" title="Funnel commerciale" subtitle="Distribuzione clienti per stato del rapporto" counter={sezNo()} delay={rev()} />
      {funnelError ? (
        <div style={{ padding: '14px 18px', background: '#FEE2E2', color: '#991B1B', borderRadius: 12, border: '1px solid #FCA5A5', marginBottom: 24, fontSize: 13 }}>
          Impossibile caricare il funnel clienti. Riprova tra qualche istante.
        </div>
      ) : (!funnelLoading && funnel.totale === 0) ? (
        <div style={{ padding: '20px 24px', background: 'rgb(var(--surface))', border: '1px solid rgb(var(--border))', borderRadius: 16, color: 'rgb(var(--muted))', fontSize: 13, marginBottom: 24 }}>
          Nessun cliente ancora registrato. Aggiungi il primo cliente per vedere il funnel commerciale.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)', gap: 16, marginBottom: 24 }}>
          <KpiCard
            accent="info" icon={I.users} label="Prospect"
            onClick={() => navigate('/clienti?stato_lifecycle=prospect')}
            value={funnelLoading ? '—' : fmt(funnel.prospect)}
            tooltip="Clienti potenziali ancora da contattare o non ancora preventivati"
            sub="Primo contatto, nessun preventivo inviato."
            delay={rev()}
          />
          <KpiCard
            accent="prev" icon={I.doc} label="In trattativa"
            onClick={() => navigate('/clienti?stato_lifecycle=trattativa')}
            value={funnelLoading ? '—' : fmt(funnel.trattativa)}
            tooltip="Clienti con almeno un preventivo inviato in attesa di risposta"
            sub="Preventivo inviato, in attesa di esito."
            delay={rev()}
          />
          <KpiCard
            accent="fin" icon={I.check} label="Attivi"
            onClick={() => navigate('/clienti?stato_lifecycle=attivo')}
            value={funnelLoading ? '—' : fmt(funnel.attivo)}
            valueColor={funnel.attivo > 0 ? '#0D6E48' : 'rgb(var(--text))'}
            tooltip="Clienti con almeno un preventivo accettato o cantiere in corso"
            sub="Preventivo accettato o lavoro avviato."
            delay={rev()}
          />
          <KpiCard
            accent="warn" icon={I.pause} label="In pausa"
            onClick={() => navigate('/clienti?stato_lifecycle=in_pausa')}
            value={funnelLoading ? '—' : fmt(funnel.in_pausa)}
            valueColor={funnel.in_pausa > 0 ? '#92500A' : 'rgb(var(--text))'}
            tooltip="Clienti che hanno ricevuto un preventivo e non hanno ancora risposto, o inattivi da oltre 90 giorni"
            sub="Da risollecitare o riattivare."
            delay={rev()}
          />
          <KpiCard
            accent="danger" icon={I.trendDn} label="Persi"
            onClick={() => navigate('/clienti?stato_lifecycle=perso')}
            value={funnelLoading ? '—' : fmt(funnel.perso)}
            valueColor={funnel.perso > 0 ? '#991B1B' : 'rgb(var(--text))'}
            tooltip="Clienti con preventivo rifiutato o trattativa chiusa negativamente"
            sub="Trattativa chiusa senza esito positivo."
            delay={rev()}
          />
        </div>
      )}

      {/* ═══ I tuoi preventivi ═══ */}
      <SectionHeader icon={I.doc} accent="prev" title="I tuoi preventivi" subtitle="Volumi, tasso di chiusura e dipendenza commerciale" counter={sezNo()} delay={rev()} />
      {/* Mobile: colonna singola. A 2 colonne su 360px la 1ª card (donut + testo
          affiancati) e la card "Concentrazione clienti" si tagliano. */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
        <div className="kpi gov-reveal"
          onClick={() => navigate('/preventivi')}
          role="button" tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/preventivi'); } }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgb(var(--text))'; e.currentTarget.style.boxShadow = '0 2px 4px rgba(43,46,54,0.06), 0 12px 30px -10px rgba(43,46,54,0.14)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgb(var(--border))'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(43,46,54,0.04), 0 8px 24px -10px rgba(43,46,54,0.08)'; }}
          style={{
          animationDelay: `${rev()}s`, background: 'rgb(var(--surface))', border: '1px solid rgb(var(--border))', borderRadius: 16,
          padding: '22px 24px', minHeight: 140, position: 'relative',
          boxShadow: '0 1px 2px rgba(43,46,54,0.04), 0 8px 24px -10px rgba(43,46,54,0.08)',
          cursor: 'pointer', transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
        }}>
          <InfoTooltip text="Su 10 preventivi che mandi, quanti vengono accettati (ultimi 90 giorni)" />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingRight: 24 }}>
            <span style={{ width: 32, height: 32, borderRadius: 10, background: ACCENT.prev.bg, color: ACCENT.prev.ink, display: 'grid', placeItems: 'center' }}>{I.check}</span>
            <span style={{ fontSize: 12, color: 'rgb(var(--muted))', fontWeight: 500 }}>Tasso di accettazione</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 22, marginTop: 14 }}>
            <Donut pct={gov.conversione_preventivi} label={gov.conversione_preventivi != null ? `${Math.round(gov.conversione_preventivi)}%` : '—'} />
            <div>
              <p style={{ margin: 0, fontSize: 13, color: 'rgb(var(--text))', fontWeight: 500 }}>
                {govLoading ? 'Caricamento…' : (gov.conversione_preventivi != null ? `${fmtPct(gov.conversione_preventivi)} accettati` : 'Nessun dato 90gg')}
              </p>
              <p style={{ fontSize: 12, color: 'rgb(var(--muted))', marginTop: 6, lineHeight: 1.5, marginBottom: 0 }}>
                Media settore 55–60%. {gov.conversione_preventivi != null && (gov.conversione_preventivi >= 55 ? 'In linea.' : 'Sotto la media.')}
              </p>
            </div>
          </div>
        </div>

        <KpiCard
          accent="warn" icon={I.users} label="Concentrazione clienti"
          onClick={() => navigate('/clienti')}
          value={govLoading ? '—' : fmtPct(gov.concentrazione_clienti)}
          valueColor={concentrazioneTone === 'crit' ? '#991B1B' : concentrazioneTone === 'warn' ? '#92500A' : 'rgb(var(--text))'}
          tooltip="Quanto del fatturato dipende dai 3 clienti principali"
          extra={<Progress pct={gov.concentrazione_clienti} tone={concentrazioneTone} />}
          sub={gov.concentrazione_clienti != null ? (gov.concentrazione_clienti >= 70 ? 'Alta concentrazione — diversifica.' : 'Buona distribuzione del rischio.') : null}
          delay={rev()}
        />

        <KpiCard
          accent="warn" icon={I.warning} label="In scadenza nei prossimi 7gg"
          onClick={() => navigate('/preventivi?stato=inviato')}
          value={govLoading ? '—' : fmt(gov.preventivi_in_scadenza)}
          valueColor={gov.preventivi_in_scadenza > 0 ? '#92500A' : 'rgb(var(--text))'}
          tooltip="Preventivi inviati con scadenza nei prossimi 7 giorni"
          sub={gov.preventivi_in_scadenza > 0
            ? `${gov.preventivi_in_scadenza} preventiv${gov.preventivi_in_scadenza === 1 ? 'o scade' : 'i scadono'} entro 7 giorni — contatta i clienti.`
            : (!govLoading ? 'Nessun preventivo in scadenza.' : null)}
          delay={rev()}
        />
      </div>

      {/* ═══ Finanziaria ═══ */}
      <SectionHeader icon={I.trend} accent="fin" title="Finanziaria" subtitle="Revenue, costi e marginalità" counter={sezNo()} delay={rev()} />
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 16, marginBottom: 16 }}>
        <KpiCard
          accent="fin" icon={I.euro} label="Fatturato"
          onClick={() => navigate('/fatture')}
          value={govLoading && data.fatturato == null ? '—' : fmtEur(data.fatturato)}
          tooltip="Totale delle fatture emesse nel periodo selezionato"
          sub={data.fatturato != null ? `Proiezione anno ≈ ${fmtEur(Math.round(data.fatturato / (new Date().getMonth() + 1) * 12))}.` : null}
          delay={rev()}
        />
        <KpiCard
          accent="danger" icon={I.trendDn} label="Costi"
          onClick={() => navigate('/fatture')}
          value={govLoading && data.costi == null ? '—' : fmtEur(data.costi)}
          valueColor="#991B1B"
          tooltip="Spese registrate nel periodo selezionato"
          sub={data.fatturato != null && data.costi != null ? `Rapporto costi/fatturato: ${fmtPct(data.costi / data.fatturato * 100)}.` : null}
          delay={rev()}
        />
        <KpiCard
          accent="fin" icon={I.clock} label="Margine operativo"
          onClick={() => navigate('/fatture')}
          value={govLoading && margineCalc == null ? '—' : fmtPct(margineCalc)}
          valueColor={margineCalc != null && margineCalc >= 52 ? '#0D6E48' : 'rgb(var(--text))'}
          tooltip="La differenza tra quanto hai fatturato e quanto hai speso, in percentuale"
          extra={<Progress pct={margineCalc} tone={margineTone} />}
          sub={margineCalc != null ? `Media settore 52–58%. ${margineCalc >= 52 ? 'In linea.' : 'Sotto la media.'}` : null}
          delay={rev()}
        />
        <KpiCard
          accent="info" icon={I.inbox} label="Da incassare"
          onClick={() => navigate('/fatture')}
          value={govLoading && data.pending == null ? '—' : fmt(data.pending)}
          valueColor="#1D4ED8"
          tooltip={`Fatture emesse e non ancora incassate (imponibile, storico). In corso: ${fmt(data.pendingInCorso || 0)} · Scadute: ${fmt(data.pendingScadute || 0)}`}
          sub={data.scadute != null && data.scadute > 0 ? `${data.scadute} scadute · sollecito urgente` : (data.scadute === 0 ? 'Nessuna fattura scaduta.' : null)}
          delay={rev()}
        />
      </div>

      {/* ── Grafico + IVA two-up ── */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '7fr 3fr', gap: 16, marginBottom: 8 }}>
        <div className="gov-reveal" style={{
          animationDelay: `${rev()}s`, background: 'rgb(var(--surface))', border: '1px solid rgb(var(--border))', borderRadius: 16,
          padding: '22px 24px', boxShadow: '0 1px 2px rgba(43,46,54,0.04), 0 8px 24px -10px rgba(43,46,54,0.08)',
          minHeight: 380, display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14, gap: 16 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 500, letterSpacing: '-0.01em', color: 'rgb(var(--text))' }}>Andamento finanziario</h3>
              <span style={{ fontSize: 12, color: 'rgb(var(--muted))', marginTop: 2, display: 'block' }}>
                Fatturato · Costi · Margine · Da incassare — {periodoParams.periodo === 'mese' ? 'per settimana' : 'per mese'}
              </span>
            </div>
          </div>
          {mensile.length > 0 ? (
            <div style={{ position: 'relative', width: '100%', height: 260, marginTop: 8 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={mensile} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="govFillFatturato" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#16C784" stopOpacity={0.28} />
                      <stop offset="100%" stopColor="#16C784" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="govFillCosti" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#EF4444" stopOpacity={0.18} />
                      <stop offset="100%" stopColor="#EF4444" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="govFillMargine" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.18} />
                      <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="govFillPending" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#F59E0B" stopOpacity={0.18} />
                      <stop offset="100%" stopColor="#F59E0B" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgb(var(--border))" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'rgb(var(--muted))' }} axisLine={false} tickLine={false} />
                  <YAxis
                    tick={{ fontSize: 10, fill: 'rgb(var(--muted))' }}
                    axisLine={false} tickLine={false}
                    tickFormatter={(v) => v >= 1000 ? `€ ${(v/1000).toFixed(0)}k` : `€ ${v}`}
                  />
                  <RTooltip
                    contentStyle={{ background: '#2C2C2A', border: 'none', borderRadius: 8, fontSize: 11, color: '#fff' }}
                    labelStyle={{ color: '#fff', fontWeight: 500, marginBottom: 4 }}
                    formatter={(value, name) => [`€ ${Number(value).toLocaleString('it-IT')}`, name]}
                  />
                  <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} iconType="line" />
                  <Area type="monotone" dataKey="fatturato" name="Fatturato" stroke="#16C784" strokeWidth={2} fill="url(#govFillFatturato)" activeDot={{ r: 4 }} />
                  <Area type="monotone" dataKey="costi" name="Costi" stroke="#EF4444" strokeWidth={2} strokeDasharray="5 4" fill="url(#govFillCosti)" activeDot={{ r: 4 }} />
                  <Area type="monotone" dataKey="margine" name="Margine" stroke="#3B82F6" strokeWidth={2} fill="url(#govFillMargine)" activeDot={{ r: 4 }} />
                  <Area type="monotone" dataKey="pending" name="Da incassare" stroke="#F59E0B" strokeWidth={2} fill="url(#govFillPending)" activeDot={{ r: 4 }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (

            <div style={{ flex: 1, display: 'grid', placeItems: 'center', color: 'rgb(var(--muted))', fontSize: 13 }}>
              Nessun dato disponibile per il periodo selezionato.
            </div>
          )}
        </div>

        <div className="gov-reveal" style={{
          animationDelay: `${rev()}s`, background: 'rgb(var(--surface))', border: '1px solid rgb(var(--border))', borderRadius: 16,
          padding: '22px 24px', boxShadow: '0 1px 2px rgba(43,46,54,0.04), 0 8px 24px -10px rgba(43,46,54,0.08)',
          minHeight: 380, display: 'flex', flexDirection: 'column',
        }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 500, letterSpacing: '-0.01em', color: 'rgb(var(--text))' }}>Posizione IVA</h3>
          <p style={{ fontSize: 12, color: 'rgb(var(--muted))', margin: '4px 0 10px' }}>Saldo netto da versare nel prossimo F24.</p>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 0', borderBottom: '1px solid rgb(var(--border))' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, fontSize: 13, fontWeight: 500, color: 'rgb(var(--text))' }}>
              <i style={{ width: 8, height: 8, borderRadius: 999, background: '#EF4444', display: 'inline-block' }} />IVA a debito
            </span>
            <span className="num" style={{ fontSize: 14, fontWeight: 500 }}>{govLoading && data.ivaDebito == null ? '—' : fmtEur(data.ivaDebito)}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 0', borderBottom: '1px solid rgb(var(--border))' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, fontSize: 13, fontWeight: 500, color: 'rgb(var(--text))' }}>
              <i style={{ width: 8, height: 8, borderRadius: 999, background: '#16C784', display: 'inline-block' }} />IVA a credito
            </span>
            <span className="num" style={{ fontSize: 14, fontWeight: 500 }}>{govLoading && data.ivaCredito == null ? '—' : fmtEur(data.ivaCredito)}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 0 0', marginTop: 'auto', borderTop: '1.5px solid rgb(var(--text))' }}>
            <span style={{ fontSize: 13, fontWeight: 500, color: 'rgb(var(--text))' }}>Saldo IVA</span>
            <span className="num" style={{ fontSize: 18, fontWeight: 500, color: data.ivaSaldo > 0 ? '#991B1B' : '#0D6E48' }}>
              {govLoading && data.ivaSaldo == null ? '—' : fmtEur(data.ivaSaldo)}
            </span>
          </div>
        </div>
      </div>

      {/* ═══ Liquidità e cassa ═══ */}
      <SectionHeader icon={I.cash} accent="cash" title="Liquidità e cassa" subtitle="Cash flow operativo e tempi di incasso" counter={sezNo()} delay={rev()} />
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', gap: 16, marginBottom: 8 }}>
        <KpiCard
          accent="fin" icon={I.euro} label="Incassi del periodo"
          onClick={() => navigate('/fatture?stato=pagata')}
          value={govLoading ? '—' : fmtEur(gov.incassi_mese)}
          valueColor="#0D6E48"
          tooltip="Totale fatture pagate nel periodo selezionato"
          sub="Fatture effettivamente incassate."
          delay={rev()}
        />
        <KpiCard
          accent="info" icon={I.funnel} label="Pipeline preventivi aperti"
          onClick={() => navigate('/preventivi')}
          value={govLoading ? '—' : fmtEur(gov.pipeline_valore)}
          valueColor="#1D4ED8"
          tooltip="Somma dei preventivi in bozza o inviati"
          sub="Bozza + inviati. Valore potenziale ancora da chiudere."
          delay={rev()}
        />
        <KpiCard
          accent="warn" icon={I.clock} label="DSO · Giorni medi incasso"
          onClick={() => navigate('/fatture')}
          value={govLoading ? '—' : (gov.dso_giorni != null ? `${gov.dso_giorni}gg` : '—')}
          valueColor={dsoTone === 'crit' ? '#991B1B' : dsoTone === 'warn' ? '#92500A' : 'rgb(var(--text))'}
          tooltip="Giorni medi tra emissione fattura e pagamento (ultimi 90 giorni)"
          extra={<Progress pct={gov.dso_giorni != null ? Math.min(100, gov.dso_giorni / 60 * 100) : 0} tone={dsoTone} />}
          sub={gov.dso_giorni != null ? `Soglia 45gg. ${gov.dso_giorni <= 45 ? 'In linea.' : 'Sopra soglia — sollecita.'}` : null}
          delay={rev()}
        />
      </div>

      {/* ── Footer ── */}
      <footer style={{ marginTop: 40, padding: '20px 0', borderTop: '1px solid rgb(var(--border))', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 11, color: 'rgb(var(--muted))' }}>
        <span>Dati aggiornati alle {nowTime()} · periodo {periodoParams.periodo}.</span>
        <span className="num" style={{ letterSpacing: '0.02em' }}>mAIstro · Controllo gestionale</span>
      </footer>
    </div>
  );
}
